import { generateColors } from '@mantine/colors-generator';
import type {
	AppShellNavbarConfiguration,
	CSSProperties,
	MantineSize,
	MantineThemeOverride,
} from '@mantine/core';
import { Button, createTheme, Modal, MultiSelect, rem, Select } from '@mantine/core';
import { DateInput, DatePickerInput } from '@mantine/dates';
// biome-ignore lint/style/noRestrictedImports: typings
import type { TFunction } from 'i18next';

import { PaperTheme } from '@apple/theme/components/paper';
import { TabTheme } from '@apple/theme/components/tab';
import { InputTheme, InputWrapperTheme } from '@apple/ui/layouts/components/input';

import { AppShellTheme } from './components/appshell';
import { CardTheme } from './components/card';
import { IndicatorTheme } from './components/indicator';
import { LoaderTheme } from './components/loader';
import { NotificationTheme } from './components/notification';
import { DrawerOverlayTheme, OverlayTheme } from './components/overlay';
import { TableTheme } from './components/table';
import { colors } from './tokens/colors';
import { materials } from './tokens/materials';
import { bodyFontFamlies, fonts, titleFontFamlies } from './tokens/typography';

declare module '@mantine/core' {
	interface MantineThemeOther {
		materials: typeof materials;
		colors: typeof colors;
		fonts: typeof fonts;
		page: {
			minWidth: CSSProperties['minWidth'];
		};
		headerHeight: string;
		footerHeight: string;
		headerBackgroundColor: CSSProperties['backgroundColor'];
		headerBackgroundColorDark: CSSProperties['backgroundColor'];
		headerBackdropFilter: CSSProperties['backdropFilter'];
		controlAccentColor: CSSProperties['color'];

		primarySidebar: {
			width: AppShellNavbarConfiguration['width'];
			rowHeight: {
				sm: CSSProperties['height'];
				md: CSSProperties['height'];
				lg: CSSProperties['height'];
			};
			textSize: {
				sm: CSSProperties['fontSize'];
				md: CSSProperties['fontSize'];
				lg: CSSProperties['fontSize'];
			};
			cellSpacing: {
				horizontal: CSSProperties['paddingLeft'];
				vertical: CSSProperties['paddingTop'];
			};
		};
		secondarySidebar: {
			width: AppShellNavbarConfiguration['width'];
		};

		getOrderStatusColor: (
			status: string | undefined,
			pendingCancellation: boolean,
			t: TFunction,
		) => CSSProperties['color'];
	}

	interface MantineThemeBreakpoints {
		xxl: number;
		xxxl: number;
	}
	type ExtendedSizes = 'xxxs' | 'xxs' | MantineSize;
	interface MantineThemeSizesOverride {
		fontSizes: Record<ExtendedSizes, string>;
		lineHeights: Record<ExtendedSizes, string>;
	}
}

// + Finish setting up theme
// https://mantine.dev/theming/default-theme/
// https://developer.apple.com/design/human-interface-guidelines/color#Specifications
// https://developer.apple.com/design/human-interface-guidelines/dark-mode
export const appleTheme: MantineThemeOverride = createTheme({
	// "scale": 1,
	// "fontSmoothing": true,
	// "focusRing": "auto",
	// autoContrast: true,
	colors: {
		blue: generateColors(colors.system.blueLight),
		cyan: generateColors(colors.system.tealLight),
		green: generateColors(colors.system.greenLight),
		indigo: generateColors(colors.system.indigoDark),
		teal: generateColors(colors.system.tealLight),
		orange: generateColors(colors.system.orangeLight),
		pink: generateColors(colors.system.pinkLight),
		purple: generateColors(colors.system.purpleLight),
		red: generateColors(colors.system.redLight),
		yellow: generateColors(colors.system.yellowLight),
		gray: [
			colors.system.gray6Light,
			colors.system.gray5Light,
			colors.system.gray4Light,
			colors.system.gray3Light,
			colors.system.gray2Light,
			colors.system.gray1Light,
			colors.system.gray2Dark,
			colors.system.gray3Dark,
			colors.system.gray4Dark,
			colors.system.gray5Dark,
			colors.system.gray6Dark,
		],
	},
	black: 'rgb(29, 29, 31)',
	// primaryShade: {
	// 	light: 5,
	// 	dark: 7,
	// },
	fontFamily: bodyFontFamlies,

	// Controls the default cursor type for interactive elements, that do not have
	// cursor: pointer styles by default. For example, `Checkbox` and `NativeSelect`.
	// "cursorType": "default",
	cursorType: 'pointer',
	// "defaultGradient": {
	//   "from": "blue",
	//   "to": "cyan",
	//   "deg": 45
	// },
	defaultRadius: 'md',
	// "activeClassName": "mantine-active",
	// "focusClassName": "",
	headings: {
		fontFamily: titleFontFamlies,
		sizes: {
			h1: {
				fontSize: fonts.largeTitle.fontSize,
				lineHeight: fonts.largeTitle.lineHeight,
				fontWeight: fonts.largeTitle.fontWeightBold,
			},
			h2: {
				fontSize: fonts.title1.fontSize,
				lineHeight: fonts.title1.lineHeight,
				fontWeight: fonts.title1.fontWeightBold,
			},
			h3: {
				fontSize: fonts.title2.fontSize,
				lineHeight: fonts.title2.lineHeight,
				fontWeight: fonts.title2.fontWeightBold,
			},
			h4: {
				fontSize: fonts.title3.fontSize,
				lineHeight: fonts.title3.lineHeight,
				fontWeight: fonts.title3.fontWeightBold,
			},
			h5: {
				fontSize: fonts.headline.fontSize,
				lineHeight: fonts.headline.lineHeight,
				fontWeight: fonts.headline.fontWeightBold,
			},
			h6: {
				fontSize: fonts.body.fontSize,
				lineHeight: fonts.body.lineHeight,
				fontWeight: fonts.body.fontWeightBold,
			},
		},
	},
	fontSizes: {
		xxxs: fonts.caption2.fontSize,
		xxs: fonts.caption1.fontSize,
		xs: fonts.footnote.fontSize,
		sm: fonts.subheadline.fontSize,
		md: fonts.callout.fontSize,
		lg: fonts.body.fontSize,
		xl: fonts.headline.fontSize,
	},
	lineHeights: {
		xxxs: fonts.caption2.lineHeight,
		xxs: fonts.caption1.lineHeight,
		xs: fonts.footnote.lineHeight,
		sm: fonts.subheadline.lineHeight,
		md: fonts.callout.lineHeight,
		lg: fonts.body.lineHeight,
		xl: fonts.headline.lineHeight,
	},
	breakpoints: {
		// xs: '36em', // 576px
		// sm: '48em', // 768px
		// md: '64em', // 1024px (Default: 62em)
		// lg: '75em', // 1200px
		// xl: '90em', // 1440px
		xxl: '120em', // 1920px
		xxxl: '160em', // 2560px
	},
	// "shadows": {
	//   "xs": "0 calc(0.0625rem * var(--mantine-scale)) calc(0.1875rem * var(--mantine-scale)) rgba(0, 0, 0, 0.05), 0 calc(0.0625rem * var(--mantine-scale)) calc(0.125rem * var(--mantine-scale)) rgba(0, 0, 0, 0.1)",
	//   "sm": "0 calc(0.0625rem * var(--mantine-scale)) calc(0.1875rem * var(--mantine-scale)) rgba(0, 0, 0, 0.05), rgba(0, 0, 0, 0.05) 0 calc(0.625rem * var(--mantine-scale)) calc(0.9375rem * var(--mantine-scale)) calc(-0.3125rem * var(--mantine-scale)), rgba(0, 0, 0, 0.04) 0 calc(0.4375rem * var(--mantine-scale)) calc(0.4375rem * var(--mantine-scale)) calc(-0.3125rem * var(--mantine-scale))",
	//   "md": "0 calc(0.0625rem * var(--mantine-scale)) calc(0.1875rem * var(--mantine-scale)) rgba(0, 0, 0, 0.05), rgba(0, 0, 0, 0.05) 0 calc(1.25rem * var(--mantine-scale)) calc(1.5625rem * var(--mantine-scale)) calc(-0.3125rem * var(--mantine-scale)), rgba(0, 0, 0, 0.04) 0 calc(0.625rem * var(--mantine-scale)) calc(0.625rem * var(--mantine-scale)) calc(-0.3125rem * var(--mantine-scale))",
	//   "lg": "0 calc(0.0625rem * var(--mantine-scale)) calc(0.1875rem * var(--mantine-scale)) rgba(0, 0, 0, 0.05), rgba(0, 0, 0, 0.05) 0 calc(1.75rem * var(--mantine-scale)) calc(1.4375rem * var(--mantine-scale)) calc(-0.4375rem * var(--mantine-scale)), rgba(0, 0, 0, 0.04) 0 calc(0.75rem * var(--mantine-scale)) calc(0.75rem * var(--mantine-scale)) calc(-0.4375rem * var(--mantine-scale))",
	//   "xl": "0 calc(0.0625rem * var(--mantine-scale)) calc(0.1875rem * var(--mantine-scale)) rgba(0, 0, 0, 0.05), rgba(0, 0, 0, 0.05) 0 calc(2.25rem * var(--mantine-scale)) calc(1.75rem * var(--mantine-scale)) calc(-0.4375rem * var(--mantine-scale)), rgba(0, 0, 0, 0.04) 0 calc(1.0625rem * var(--mantine-scale)) calc(1.0625rem * var(--mantine-scale)) calc(-0.4375rem * var(--mantine-scale))"
	// },
	other: {
		colors,
		materials,
		fonts,
		page: {
			minWidth: rem(380),
		},
		headerHeight: rem(44),
		footerHeight: rem(84),
		headerForegroundColor: 'rgba(255, 255, 255, .8)',
		headerForegroundHoverColor: 'rgba(255, 255, 255, 1)',
		headerBackgroundColor: 'rgba(22, 22, 23, .8)',
		headerBackgroundColorDark: 'rgba(0, 0, 0, .7)',
		headerBackdropFilter: 'saturate(180%) blur(30px)',
		borderColor: 'rgb(229, 229, 234)',
		borderColorDark: 'rgb(45,45, 50)',
		controlAccentColor: 'rgb(10, 132, 255)',
		primarySidebar: {
			width: 260,
			rowHeight: {
				sm: '24pt',
				md: '28pt',
				lg: '32pt',
			},
			textSize: {
				sm: '11pt',
				md: '13pt',
				lg: '15pt',
			},
			cellSpacing: {
				horizontal: '17pt',
				vertical: 0,
			},
		},
		secondarySidebar: {
			width: 380,
		},
		getOrderStatusColor: (status, pendingCancellation, t) => {
			if (pendingCancellation) {
				return 'var(--mantine-color-yellow-filled)';
			}

			// This is a bit of a hack since the backend doesn't return the status as an enum...
			switch (status) {
				case t('order:orderStatus.inProcess'):
				case t('order:orderStatus.shipped'):
					return 'var(--mantine-color-green-9)';
				case t('order:orderStatus.backordered'):
				case t('order:orderStatus.rejected'):
					return 'var(--mantine-color-red-filled)';
				case t('order:orderStatus.orderReview'):
					return 'var(--mantine-color-yellow-filled)';
				case t('order:orderStatus.cancelled'):
				case t('order:orderStatus.canceled'):
					return 'var(--mantine-color-gray-filled)';
				default:
					return 'var(--mantine-color-blue-filled)';
			}
		},
		cartSidebarOffset: 'calc(var(--mantine-spacing-xl) * -1 - 20px)',
	},
	components: {
		AppShell: AppShellTheme,
		Card: CardTheme,
		Paper: PaperTheme,
		Loader: LoaderTheme,
		Indicator: IndicatorTheme,
		Input: InputTheme,
		InputWrapper: InputWrapperTheme,
		Select: Select.extend({
			defaultProps: {
				checkIconPosition: 'right',
				searchable: true,
			},
		}),
		MultiSelect: MultiSelect.extend({
			defaultProps: {
				searchable: true,
			},
			vars: () => ({
				wrapper: {
					'--input-padding-y': '0',
					'--input-height': 'var(--input-size)',
				},
			}),
		}),
		Notification: NotificationTheme,

		DateInput: DateInput.extend({
			styles: {
				input: {
					paddingInline: rem(8),
				},
			},
		}),

		DatePickerInput: DatePickerInput.extend({
			styles: {
				input: {
					paddingInline: rem(8),
				},
			},
		}),

		Table: TableTheme,
		Overlay: OverlayTheme,
		Drawer: DrawerOverlayTheme,
		Tabs: TabTheme,
		Button: Button.extend({ styles: { root: { borderRadius: rem(10) } } }),

		Modal: Modal.extend({
			styles: (_theme, _props, _ctx) => ({
				header: {
					backgroundColor: materials.chromeDark.background,
					backgroundBlendMode: materials.chromeDark.backgroundBlendMode,
					backdropFilter: materials.chromeDark.backdropFilter,
				},
				body: {
					backgroundColor: materials.regularDark.background,
					backgroundBlendMode: materials.regularDark.backgroundBlendMode,
					backdropFilter: materials.regularDark.backdropFilter,
				},
			}),
		}),
	},
});
